import listaServicos from '@/data/fiscal/lista_servicos_nacionais.json';
import correlacaoNbs from '@/data/fiscal/correlacao_lc116_nbs.json';
import tabelaCst from '@/data/fiscal/tabela_cst_ibs_cbs.json';
import tabelaClassTrib from '@/data/fiscal/tabela_cclasstrib.json';
import atividadesSP from '@/data/fiscal/atividades_sao_paulo.json';
import atividadesFortaleza from '@/data/fiscal/atividades_fortaleza.json';
import lc214Anexos from '@/data/fiscal/lc214_anexos.json';
import lc214Simples from '@/data/fiscal/lc214_simples_nacional.json';

export interface ServicoLC116 {
  codigo: string;
  descricao: string;
}

export interface CorrelacaoNBS {
  /** Item da LC 116 no formato 4 digitos (ex.: "0101"). */
  lc116: string;
  descricao: string;
  nbs: string;
  cClassTrib: string;
  cIndOp: string;
}

export interface CstIbsCbs {
  codigo: string;
  descricao: string;
}

export interface ClassTrib {
  codigo: string;
  descricao: string;
}

export interface AtividadeMunicipal {
  codigo: string;
  descricao: string;
  aliquota_iss: number;
  ibge: string;
  /** Item da lista LC 116 correlacionado (4 digitos). */
  item_lista: string;
  /** NBS sugerido (Anexo VIII). */
  nbs: string;
  /** cClassTrib IBS/CBS sugerido. */
  cClassTrib: string;
}

// --- LC 214/2025 anexos (Reforma Tributaria — IBS/CBS) ---------------------

export type LC214Beneficio =
  | 'reducao_zero'
  | 'reducao_60'
  | 'reducao_100'
  | 'aliquota_referencia'
  | 'imposto_seletivo';

export interface LC214AnexoItem {
  item: string;
  descricao: string;
  /** Codigo fiscal (NBS, NCM/SH, ...) quando o anexo tem coluna de codigo. */
  codigo?: string;
}

export interface LC214Anexo {
  /** Numeral romano do anexo (I, II, ... XVII). */
  anexo: string;
  /** Titulo oficial do anexo, reproduzido do DOU. */
  titulo: string;
  /** Beneficio derivado do titulo oficial; null quando nao explicito. */
  beneficio: LC214Beneficio | null;
  /** "lista" (ITEM/DESCRICAO[/codigo]) ou "generico" (colunas literais). */
  formato: 'lista' | 'generico';
  /** Cabecalhos literais das colunas do DOU. */
  colunas: string[];
  /** Rotulo da coluna de codigo (ex.: "NBS", "NCM/SH") quando existir. */
  codigo_tipo?: string | null;
  /** Itens, para anexos de formato "lista". */
  itens?: LC214AnexoItem[];
  /** Linhas com colunas literais, para anexos de formato "generico". */
  linhas?: Array<Record<string, string>>;
}

/** Valores fixos do MEI, ja convertidos de "R$ x,yz" para numero. */
export interface SimplesMEIValores {
  icms: number | null;
  iss: number | null;
  cbs: number | null;
  ibs: number | null;
  total: number | null;
}

/** Tabela do Simples Nacional (Anexos XVIII–XXIII). */
export interface SimplesNacionalTabela {
  /** Anexo LC 214 (XVIII–XXIII). */
  wrapper: string;
  /** Anexo interno da LC 123 (I–VII). */
  lc123_anexo: string | null;
  /** Titulo/vigencia da tabela, reproduzido do DOU. */
  caption: string;
  tipo: 'aliquota_por_faixa' | 'reparticao_tributos' | 'mei_valores' | 'outro';
  /**
   * Rotulos semanticos alinhados 1:1 com cada celula de `linhas`. Os tributos
   * variam por anexo/vigencia (ICMS vs ISS, com/sem IPI/CPP), por isso os nomes
   * vem do proprio cabecalho da lei, nao de chaves fixas.
   */
  colunas: string[];
  /** Linhas de cabecalho literais do DOU (1 ou 2, por celulas mescladas). */
  cabecalho: string[][];
  /** Linhas de dados como arrays posicionais alinhados a `colunas`. */
  linhas: string[][];
  /** Presente apenas quando tipo === "mei_valores": valores em R$ ja numericos. */
  valores?: SimplesMEIValores;
}

export const IBGE_SAO_PAULO = '3550308';
export const IBGE_FORTALEZA = '2304400';

export const MUNICIPIOS_DISPONIVEIS: Array<{ ibge: string; nome: string; uf: string }> = [
  { ibge: IBGE_SAO_PAULO, nome: 'Sao Paulo', uf: 'SP' },
  { ibge: IBGE_FORTALEZA, nome: 'Fortaleza', uf: 'CE' },
];

// --- Raw shapes dos JSONs extraidos (chave "itens", campos oficiais) --------

interface ServicoRaw {
  code: string;
  title: string;
}
interface CorrelacaoRaw {
  codigo_servico: string;
  descricao_item: string;
  codigo_nbs: string;
  codigo_indicador_operacao: string;
  cclasstrib: string;
}
interface CodigoDescricaoRaw {
  codigo: string;
  descricao: string;
}
interface AtividadeSpRaw {
  codigo_servico: string;
  descricao: string;
  aliquota_iss: number;
  item_lista: string;
  codigo_nbs: string;
  cclasstrib: string;
}
interface AtividadeFortalezaRaw {
  codigo_tributario_municipio: string;
  descricao: string;
  aliquota_iss: number;
  item_lista: string;
  codigo_nbs: string;
  cclasstrib: string;
}

const servicos: ServicoLC116[] = (listaServicos as ServicoRaw[]).map((s) => ({
  codigo: s.code,
  descricao: s.title,
}));

const correlacoes: CorrelacaoNBS[] = (
  correlacaoNbs as { itens: CorrelacaoRaw[] }
).itens.map((c) => ({
  lc116: c.codigo_servico,
  descricao: c.descricao_item,
  nbs: c.codigo_nbs,
  cIndOp: c.codigo_indicador_operacao,
  cClassTrib: c.cclasstrib,
}));

const cstList: CstIbsCbs[] = (tabelaCst as { itens: CodigoDescricaoRaw[] }).itens;
const classTribList: ClassTrib[] = (tabelaClassTrib as { itens: CodigoDescricaoRaw[] }).itens;
const anexosLC214: LC214Anexo[] = (lc214Anexos as unknown as { anexos: LC214Anexo[] }).anexos;
const simplesNacionalTabelas: SimplesNacionalTabela[] = (
  lc214Simples as unknown as { tabelas: SimplesNacionalTabela[] }
).tabelas;

const atividadesSpNormalized: AtividadeMunicipal[] =
  (atividadesSP as { itens: AtividadeSpRaw[] }).itens.map((a) => ({
    codigo: a.codigo_servico,
    descricao: a.descricao,
    aliquota_iss: a.aliquota_iss,
    ibge: IBGE_SAO_PAULO,
    item_lista: a.item_lista,
    nbs: a.codigo_nbs,
    cClassTrib: a.cclasstrib,
  }));

const atividadesFortalezaNormalized: AtividadeMunicipal[] =
  (atividadesFortaleza as { itens: AtividadeFortalezaRaw[] }).itens.map((a) => ({
    codigo: a.codigo_tributario_municipio,
    descricao: a.descricao,
    aliquota_iss: a.aliquota_iss,
    ibge: IBGE_FORTALEZA,
    item_lista: a.item_lista,
    nbs: a.codigo_nbs,
    cClassTrib: a.cclasstrib,
  }));

function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

export function searchServicos(query: string): ServicoLC116[] {
  const q = normalize(query.trim());
  if (!q) return servicos;
  return servicos.filter(
    (s) => normalize(s.descricao).includes(q) || s.codigo.includes(q),
  );
}

export function getCorrelacaoByLC116(codigo: string): CorrelacaoNBS | null {
  // Aceita "01.01" (formato LC 116) ou "0101" (formato do dado) — compara so digitos.
  const target = codigo.replace(/\D/g, '');
  if (!target) return null;
  return correlacoes.find((c) => c.lc116.replace(/\D/g, '') === target) ?? null;
}

export function searchCorrelacoes(query: string): CorrelacaoNBS[] {
  const q = normalize(query.trim());
  if (!q) return correlacoes;
  return correlacoes.filter(
    (c) =>
      c.lc116.includes(q) ||
      normalize(c.descricao).includes(q) ||
      normalize(c.nbs).includes(q) ||
      c.cClassTrib.includes(q),
  );
}

export function getCstOptions(): CstIbsCbs[] {
  return cstList;
}

export function getClassTribByCodigo(codigo: string): ClassTrib | null {
  const target = codigo.trim();
  return classTribList.find((c) => c.codigo === target) ?? null;
}

export function getAtividadesMunicipio(ibge: string): AtividadeMunicipal[] {
  if (ibge === IBGE_SAO_PAULO) return atividadesSpNormalized;
  if (ibge === IBGE_FORTALEZA) return atividadesFortalezaNormalized;
  return [];
}

export function searchAtividadesMunicipio(
  ibge: string,
  query: string,
): AtividadeMunicipal[] {
  const list = getAtividadesMunicipio(ibge);
  const q = normalize(query.trim());
  if (!q) return list;
  return list.filter(
    (a) => normalize(a.descricao).includes(q) || a.codigo.includes(q),
  );
}

// --- LC 214/2025 anexos ----------------------------------------------------

export function getLC214Anexos(): LC214Anexo[] {
  return anexosLC214;
}

export function getLC214Anexo(anexo: string): LC214Anexo | null {
  const target = anexo.trim().toUpperCase();
  return anexosLC214.find((a) => a.anexo === target) ?? null;
}

export interface LC214ItemMatch extends LC214AnexoItem {
  anexo: string;
  beneficio: LC214Beneficio | null;
}

/**
 * Busca itens (produtos/servicos) em todos os anexos de formato "lista" por
 * descricao ou codigo. Opcionalmente filtra por beneficio (ex.: "reducao_zero").
 */
export function searchLC214Itens(
  query: string,
  beneficio?: LC214Beneficio,
): LC214ItemMatch[] {
  const q = normalize(query.trim());
  const results: LC214ItemMatch[] = [];
  for (const anexo of anexosLC214) {
    if (anexo.formato !== 'lista' || !anexo.itens) continue;
    if (beneficio && anexo.beneficio !== beneficio) continue;
    for (const item of anexo.itens) {
      const matches =
        !q ||
        normalize(item.descricao).includes(q) ||
        (item.codigo ? normalize(item.codigo).includes(q) : false);
      if (matches) {
        results.push({ ...item, anexo: anexo.anexo, beneficio: anexo.beneficio });
      }
    }
  }
  return results;
}

// --- Simples Nacional (Anexos XVIII–XXIII) ---------------------------------

export function getSimplesNacionalTabelas(): SimplesNacionalTabela[] {
  return simplesNacionalTabelas;
}

/** Anexos LC 214 (XVIII–XXIII) que contem tabelas do Simples Nacional. */
export function getSimplesNacionalWrappers(): string[] {
  return [...new Set(simplesNacionalTabelas.map((t) => t.wrapper))];
}

export function getSimplesNacionalTabelasByWrapper(
  wrapper: string,
): SimplesNacionalTabela[] {
  const target = wrapper.trim().toUpperCase();
  return simplesNacionalTabelas.filter((t) => t.wrapper === target);
}

/** Valores fixos do MEI por periodo de vigencia (ja numericos). */
export function getSimplesMEIValores(): SimplesNacionalTabela[] {
  return simplesNacionalTabelas.filter((t) => t.tipo === 'mei_valores');
}
